// Centralized registry of pending-flush callbacks.
// Each store or composable with a throttled write registers its flush function here.
// A single router.beforeEach guard calls flushAll() before every navigation,
// ensuring no in-flight throttled writes are lost on route change.

type FlushFn = () => void | Promise<void>;

const registry = new Set<FlushFn>();

export function registerFlush(fn: FlushFn): void {
    registry.add(fn);
}

export function unregisterFlush(fn: FlushFn): void {
    registry.delete(fn);
}

export function flushAll(): void {
    registry.forEach((fn) => void fn());
}

/**
 * flushAll(), but waits for the flushes that are asynchronous.
 *
 * Most registrants only push a throttled write into the sync queue, so the
 * caller's own `syncStore.sync()` is what actually persists them and the
 * fire-and-forget `flushAll()` is enough. Some do not go through the queue at
 * all — `ObjectivesBoard` saves through `objectives-store.updateObjective`,
 * which calls the API directly — so there is nothing for `sync()` to wait on
 * and the write is still in flight when the caller moves on.
 *
 * That only matters when what comes next is destructive or changes where
 * writes are routed: leaving a draft clears `x-thread-revision`, and the
 * leave may go on to delete the draft's branch outright. Both need the writes
 * to have LANDED, not merely been started. Rejections are swallowed — a
 * failed save reports itself through its own toast, and must not take the
 * caller's exit down with it.
 */
export async function flushAllSettled(): Promise<void> {
    await Promise.allSettled([...registry].map((fn) => fn()));
}
